import api from './api';

export interface ProjectManagerRef {
  name: string;
  avatar: string;
}

export interface ProjectListItem {
  id: number;
  name: string;
  client: string | null;
  description: string | null;
  creationDate?: string | null;
  casesCount: number;
  tasksCount: number;
  manager: ProjectManagerRef | null;
  tags: string[];
  hasNewMessages: boolean;
  hasOverdueTasks: boolean;
  isFavorite?: boolean;
  isPinned?: boolean;
  // Признаки участия текущего пользователя в проекте (для вкладок "Руковожу" / "Участвую").
  isManager?: boolean;
  isExecutor?: boolean;
  isObserver?: boolean;
  isParticipating?: boolean;
  isCompleted: boolean;
  completedDaysAgo: number | null;
  isJudicial: boolean;
}

export interface ProjectsListResponse {
  data: ProjectListItem[];
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

export interface ProjectsStatistics {
  total_projects: number;
  projects_this_month: number;
}

export interface ProjectDetailsResponse {
  id: number;
  name: string;
  client: string | null;
  client_id: number;
  description: string | null;
  practice_area?: string | null;
  practice_areas?: string[];
  start_date?: string | null;
  type?: string;
  tags: string[];
  is_private: boolean;
  is_completed: boolean;
  chat_enabled: boolean;
  tasksCount?: number;
  isJudicial?: boolean;
  manager: ProjectManagerRef & { id: number };
  executors?: Array<{ id: number; name: string; avatar?: string | null }>;
  observers?: Array<{ id: number; name: string; avatar?: string | null }>;
  executor_ids?: number[];
  observer_ids?: number[];
}

export const projectsService = {
  async getProjects(params: {
    participant_user_id?: number;
    page?: number;
    per_page?: number;
    client?: string;
    client_id?: number | string;
    projectManager?: string;
    tag?: string;
    case_practices?: string[];
    case_id?: number | string;
    case_executor_id?: number | string;
  } = {}): Promise<ProjectsListResponse> {
    const url = new URLSearchParams();
    if (params.page) url.append('page', params.page.toString());
    if (params.per_page) url.append('per_page', params.per_page.toString());
    if (params.participant_user_id) url.append('participant_user_id', String(params.participant_user_id));
    if (params.client_id != null && String(params.client_id).trim() !== '') {
      url.append('client_id', String(params.client_id).trim());
    }
    if (params.client) url.append('client', params.client);
    if (params.projectManager) url.append('projectManager', params.projectManager);
    if (params.tag) url.append('tag', params.tag);
    if (params.case_id != null && String(params.case_id).trim() !== '') {
      url.append('case_id', String(params.case_id).trim());
    }
    if (params.case_executor_id != null && String(params.case_executor_id).trim() !== '') {
      url.append('case_executor_id', String(params.case_executor_id).trim());
    }
    if (params.case_practices && params.case_practices.length > 0) {
      url.append('case_practices', params.case_practices.join(','));
    }

    const { data } = await api.get<ProjectsListResponse>(`/projects?${url.toString()}`);
    return data;
  },

  async getProjectsArchive(params: {
    page?: number;
    per_page?: number;
    client?: string;
    projectManager?: string;
    tag?: string;
    case_practices?: string[];
    case_id?: number | string;
    case_executor_id?: number | string;
  } = {}): Promise<ProjectsListResponse> {
    const url = new URLSearchParams();
    if (params.page) url.append('page', params.page.toString());
    if (params.per_page) url.append('per_page', params.per_page.toString());
    if (params.client) url.append('client', params.client);
    if (params.projectManager) url.append('projectManager', params.projectManager);
    if (params.tag) url.append('tag', params.tag);
    if (params.case_id != null && String(params.case_id).trim() !== '') {
      url.append('case_id', String(params.case_id).trim());
    }
    if (params.case_executor_id != null && String(params.case_executor_id).trim() !== '') {
      url.append('case_executor_id', String(params.case_executor_id).trim());
    }
    if (params.case_practices && params.case_practices.length > 0) {
      url.append('case_practices', params.case_practices.join(','));
    }

    const { data } = await api.get<ProjectsListResponse>(`/projects/archive?${url.toString()}`);
    return data;
  },

  async getProject(id: number): Promise<ProjectDetailsResponse> {
    const { data } = await api.get<ProjectDetailsResponse>(`/projects/${id}`);
    return data;
  },

  async createProject(payload: {
    name: string;
    client_id: number;
    manager_id: number;
    description?: string | null;
    practice_area?: string | null;
    practice_areas?: string[];
    type?: string;
    tags?: string[];
    is_private?: boolean;
    chat_enabled?: boolean;
    executor_ids?: number[];
    observer_ids?: number[];
    is_completed?: boolean;
    completed_at?: string | null;
  }): Promise<{ data: any }> {
    const { data } = await api.post<{ message: string; data: any }>(`/projects`, payload);
    return data;
  },

  async updateProject(id: number, payload: Record<string, any>): Promise<{ message: string; data: any }> {
    const { data } = await api.put<{ message: string; data: any }>(`/projects/${id}`, payload);
    return data;
  },

  async getStatistics(): Promise<ProjectsStatistics> {
    const { data } = await api.get<ProjectsStatistics>('/projects/statistics');
    return data;
  },

  async toggleProjectFavorite(projectId: number): Promise<{ isFavorite: boolean; isPinned: boolean }> {
    const { data } = await api.post<{ isFavorite: boolean; isPinned: boolean }>(`/projects/${projectId}/toggle-favorite`);
    return data;
  },

  async toggleProjectPinned(projectId: number): Promise<{ isPinned: boolean; isFavorite: boolean }> {
    const { data } = await api.post<{ isPinned: boolean; isFavorite: boolean }>(`/projects/${projectId}/toggle-pinned`);
    return data;
  },

  /**
   * Скачать Excel по списку проектов (GET, blob). Параметры — как в getProjects.
   */
  async downloadProjectsExcel(params: {
    participant_user_id?: number;
    tab?: string;
    client?: string;
    projectManager?: string;
    tag?: string;
    case_practices?: string[];
    case_id?: number | string;
    case_executor_id?: number | string;
  } = {}): Promise<void> {
    const url = new URLSearchParams();
    if (params.tab && params.tab !== 'all') url.append('tab', params.tab);
    if (params.participant_user_id) url.append('participant_user_id', String(params.participant_user_id));
    if (params.client) url.append('client', params.client);
    if (params.projectManager) url.append('projectManager', params.projectManager);
    if (params.tag) url.append('tag', params.tag);
    if (params.case_id != null && String(params.case_id).trim() !== '') {
      url.append('case_id', String(params.case_id).trim());
    }
    if (params.case_executor_id != null && String(params.case_executor_id).trim() !== '') {
      url.append('case_executor_id', String(params.case_executor_id).trim());
    }
    if (params.case_practices && params.case_practices.length > 0) {
      url.append('case_practices', params.case_practices.join(','));
    }

    const path = `/projects/export/excel${url.toString() ? `?${url.toString()}` : ''}`;
    const res = await api.get<Blob>(path, { responseType: 'blob' });

    const blob = res.data;
    const ctype = String(res.headers['content-type'] || '');
    if (ctype.includes('application/json')) {
      const text = await blob.text();
      let msg = 'Не удалось сформировать Excel';
      try {
        const j = JSON.parse(text) as { message?: string };
        if (j?.message) msg = j.message;
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }

    const header = String(res.headers['content-disposition'] || '');
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
    let filename: string | null = null;
    if (utf8?.[1]) {
      try {
        filename = decodeURIComponent(utf8[1].trim());
      } catch {
        filename = utf8[1].trim();
      }
    }
    if (!filename) {
      const simple = /filename="([^"]+)"/i.exec(header) || /filename=([^;\s]+)/i.exec(header);
      filename = simple?.[1]?.trim() ?? null;
    }

    const name = filename || 'projects.xlsx';
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  },
};

